import { inject, Injectable } from '@angular/core';
import { DataService } from './data.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IIbptImport, IIbptImportResult } from '../interface/ibpt-import';
import { firstValueFrom } from 'rxjs';
import { AddressService } from './address.service';
import { IBPT_TYPE } from '../component/page/company/settings/xls-csv-importer/ItemToBeImported/ItemToBeImported';

@Injectable({
  providedIn: 'root'
})
export class IbptImportService {

  private _isFromMatrix = false;
  private _baseUrl: string = `${environment.mkgoURL}/api/v1/ibpts`;
  private _http = inject(HttpClient);
  private _dataService = inject(DataService);
  private _addressService = inject(AddressService);

  /** Imports the IBPT table of one UF, replacing the rows previously imported for it */
  async saveIbtp(uf: string, items: IIbptImport[]): Promise<IIbptImportResult> {
    const url = this._baseUrl;
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix));
    return await firstValueFrom(this._http.post<IIbptImportResult>(url, JSON.stringify({ uf, ibpts: items }), options));
  }

  /** Two-letter UF of the current company's address, used to scope IBPT lookups */
  async getCompanyUf(): Promise<string | undefined> {
    const stateId = this._dataService.company?.address?.state as string;
    const state = await firstValueFrom(this._addressService.findState(stateId));
    return state?.code;
  }

  /**
   * IBPT row of an NCM in the given UF (defaults to the current company's UF).
   * Returns nothing when there is no code or no UF to scope the lookup.
   */
  async getIbptData(code: number | string, uf?: string): Promise<{
    nationalFederal: number;
    state: number;
    municipal: number;
    key: string;
  }> {
    if (code === null || code === undefined || code === "") {
      return;
    }

    const lookupUf = uf ?? await this.getCompanyUf();
    if (!lookupUf) {
      return;
    }

    const url = `${this._baseUrl}/code/${code}`;
    const params = new HttpParams()
      .set('uf', lookupUf)
      .set('type', IBPT_TYPE.NCM);
    const options = await firstValueFrom(this._dataService.httpOptions(this._isFromMatrix, params));
    const response = await firstValueFrom(this._http.get(url, options));

    return (response as any).ibpt;
  }
}
